<template>
    <section class="shipping">
        <div class="custom-container">
            <div class="row">
                <div class="col-12" :class="{loading: loading}">
                    <div v-if="authenticated">
                        <div v-if="redirect_online_payment" class="shipping__inner">
                            <RedirectOnlinePayment :form="place_order.payment.form" />
                        </div>
                        <div v-else class="shipping__inner">
                            <div class="shipping__main">
                                <template v-if="step === 'shipping'">
                                    <Shipping
                                        v-if="user.first_name"
                                        :shippingMethods="shipping_methods"
                                        v-model:addressId="address_id"
                                        v-model:shippingMethod="shipping_method"
                                        v-model:shippingPeriodId="shipping_period_id"
                                    />
                                    <CompleteProfile v-else />
                                </template>
                                <template v-else-if="step === 'payment'">
                                    <Payment
                                        v-model:paymentMethod="payment_method"
                                        v-model:coupon="coupon"
                                        :coupons="proforma_invoice.coupons"
                                        :address="proforma_invoice.address"
                                        :shipping_method="proforma_invoice.shipping_method"
                                        :methods="proforma_invoice.payment_methods"
                                        @back="back"
                                    />
                                </template>
                            </div>
                            <div class="shipping__aside">
                                <Totaling
                                    :invoice="invoice"
                                    :proceedable="proceedable"
                                    :step="step"
                                    @next="next"
                                />
                                <Note v-model="note" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <TotalingMobile
        :invoice="invoice"
        :proceedable="proceedable"
        :step="step"
        @next="next"
    />
</template>

<script>
import Shipping from "./Shipping";
import CompleteProfile from "./CompleteProfile";
import Totaling from "./Totaling";
import Note from "./Note";
import {CartMixin} from "../cart/CartMixin";
import Payment from "./Payment";
import RedirectOnlinePayment from "./RedirectOnlinePayment";
import TotalingMobile from "./TotalingMobile.vue";

export default {
    name: "Checkout",
    components: {TotalingMobile, RedirectOnlinePayment, Payment, Note, Totaling, CompleteProfile, Shipping},
    mixins: [CartMixin],
    data: () => ({
        loading: false,
        step: 'shipping',
        address_id: null,
        shipping_method: null,
        shipping_period_id: null,
        proforma_invoice: null,
        payment_method: null,
        coupon: null,
        note: '',
        place_order: null,
        redirect_online_payment: false,
        shipping_methods: [],
    }),
    computed: {
        user() {
            return this.getUser();
        },
        authenticated() {
            return this.isAuthenticated();
        },
        proceedable() {
            if (this.step === 'shipping') {
                return this.shipping_period_id || (this.shipping_method?.mandatory_period === false && !this.shipping_method.periods?.length)
            } else if (this.step === 'payment') {
                return !!this.payment_method;
            }
            return false;
        },
        invoice() {
            if (this.proforma_invoice === null) {
                if (this.totals?.total) {
                    return {
                        sub_total: this.totals.subtotal,
                        shipping: this.shipping_method !== null ? this.shipping_method?.cost : null,
                        discount: this.totals.discount,
                        discount_percentage: this.totals.discount_percentage,
                        coupon_discount: null,
                        total: this.totals.total,
                    }
                }
            } else {
                return {
                    sub_total: this.proforma_invoice.totals.subtotal,
                    shipping: this.proforma_invoice.totals.shipping_cost,
                    discount: this.proforma_invoice.totals.line_discount,
                    discount_percentage: this.proforma_invoice.totals.line_discount_percentage,
                    coupon_discount: this.proforma_invoice.totals.coupon_discount,
                    total: this.proforma_invoice.totals.total,
                }
            }
            return {
                sub_total: null,
                shipping: null,
                discount: null,
                discount_percentage: null,
                coupon_discount: null,
                total: null
            };
        }
    },
    watch: {
        async coupon() {
            this.loading = true;
            await this.getProformaInvoice();
            this.loading = false;
        },
        authenticated() {
            if (this.authenticated) {
                this.checkIsLoading();
            }
        },
        address_id() {
            this.shipping_method = null;
            this.shipping_period_id = null;
            this.requestShippingMethods();
        }
    },
    methods: {
        async loadEssentials() {
            this.loading = true;
            this.loading = false;
        },
        requestShippingMethods() {
            this.loading = true;
            const token = localStorage.getItem('_token');
            let config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };
            this.axios.get(this.route('api.checkout.shipping_methods', this.address_id), config).then(response => {
                this.shipping_methods = response.data.data;
                this.loading = false;
            });
        },
        async getProformaInvoice() {
            const token = localStorage.getItem('_token');
            let config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };
            await this.axios.get(this.route('api.checkout.proforma_invoice', {
                address_id: this.address_id,
                shipping_method: this.shipping_method.key,
                shipping_period_id: this.shipping_period_id,
                coupon: this.coupon
            }), config)
            .then(response => {
                this.proforma_invoice = response.data.data;
                this.step = 'payment';
            })
            .catch(error => {
                if (typeof error.response.data.coupon !== "undefined") {
                    this.errorToast("کوپن تخفیف وارد شده وجود ندارد.");
                    return;
                }
                this.errorToast(null, error);
            });
        },
        async placeOrder() {
            const token = localStorage.getItem('_token');
            let config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };
            const data = {
                address_id: this.address_id,
                shipping_method: this.shipping_method.key,
                shipping_period_id: this.shipping_period_id,
                coupon: this.coupon,
                payment_method: this.payment_method,
                description: this.note
            };
            await this.axios.post(this.route('api.checkout.place_order'), data, config)
                .then(response => {
                    this.place_order = response.data.data;
                    this.step = 'final';
                    this.next();
                })
                .catch(error => {
                    if (typeof error.response.data.coupon !== 'undefined') {
                        this.errorToast("کوپن تخفیف وارد شده وجود ندارد.");
                        return;
                    }
                    this.errorToast(null, error);
                });
        },
        async next() {
            if (this.step === 'shipping') {
                this.loading = true;
                await this.getProformaInvoice();
                this.loading = false;
            } else if (this.step === 'payment') {
                this.loading = true;
                await this.placeOrder();
                this.loading = false;
            } else if (this.step === 'final') {
                this.handleOrderPayment();
            }
        },
        back() {
            this.proforma_invoice = null;
            this.payment_method = null;
            this.coupon = null;
            this.step = "shipping";
        },
        handleOrderPayment() {
            if (typeof this.place_order.payment.form !== "undefined") {
                this.redirect_online_payment = true;
            } else {
                window.location.href = '/profile/orders/'+this.place_order.order.id;
            }
        }
    },
    mounted() {
        this.loadEssentials();
    }
}
</script>

<style scoped>

</style>
